import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
	type ColumnOrderState,
	getCoreRowModel,
	type RowSelectionState,
	type SortingState,
	useReactTable,
	type VisibilityState,
} from "@tanstack/react-table";
import { Filter, Play } from "lucide-react";
import {
	type Dispatch,
	type SetStateAction,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { AutoCombineTable } from "@/features/attorney/views/Entries/AutoCombineTable";
import {
	AUTO_COLUMN_LABELS,
	AUTO_DEFAULT_COLUMN_ORDER,
	AUTO_DEFAULT_COLUMN_VISIBILITY,
	autoColumnOrderParser,
	autoVisibilityParser,
	getAutoColumns,
} from "@/features/attorney/views/Entries/autoCombineColumns";
import { DEFAULT_FILTERS } from "@/features/attorney/views/Entries/filters/defaults";
import {
	type FieldOptions,
	FilterMenu,
} from "@/features/attorney/views/Entries/filters/FilterMenu";
import { normalizeOptions } from "@/features/attorney/views/Entries/filters/fields";
import type {
	EntryFilterState,
	EnumFilterField,
} from "@/features/attorney/views/Entries/filters/types";
import {
	filterStateParser,
	serializeEntryFilters,
} from "@/features/attorney/views/Entries/filters/urlState";
import {
	type EntryStatsTotals,
	StatsBar,
} from "@/features/attorney/views/Entries/StatsBar";
import { SearchBar } from "@/features/attorney/views/Entries/search/SearchBar";
import { ColumnsMenu } from "@/features/attorney/views/Entries/toolbar/ColumnsMenu";
import {
	DEFAULT_SORTING,
	sortingParser,
} from "@/features/attorney/views/Entries/viewParams";
import {
	type EntryGroupListWire,
	type EntryGroupWire,
	entryGroupToEntryGroupEntry,
	useMattersCache,
} from "@/shared/cache";
import { useApi } from "@/shared/utils/request";
import { DatePicker } from "@/v2/components/date-picker/DatePicker";
import { Button } from "@/v2/components/ui/button";
import { TooltipProvider } from "@/v2/components/ui/tooltip";
import {
	type AdminEntryQuery,
	buildAdminEntrySort,
	buildAdminEntryWhere,
	type EntryGroupStats,
	mapAdminEntryFilters,
	mapAdminEntryStats,
	matterMap,
} from "../entries/query";

const PAGE_LIMIT = 100;
const ADMIN_DEFAULT_FILTERS: EntryFilterState = {
	status: [...DEFAULT_FILTERS.status, "exported"],
};
const EMPTY_SELECTION: RowSelectionState = {};
const EMPTY_IDS = new Set<string>();
const EMPTY_TIMEKEEPERS = [] as const;
const NOOP = () => {};
const READ_ONLY_MOVE_MODE = {
	active: false,
	isValidTarget: () => false,
	onMove: NOOP,
};

function formatLocalDate(date: Date): string {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
}

function parseLocalDate(value: string | null, fallback: Date): Date {
	const match = value?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
	if (!match) return fallback;
	const parsed = new Date(
		Number(match[1]),
		Number(match[2]) - 1,
		Number(match[3]),
	);
	return Number.isNaN(parsed.getTime()) ? fallback : parsed;
}

function normalizeFilters(filters: EntryFilterState | null): EntryFilterState {
	return {
		...filters,
		status: filters?.status ?? [...ADMIN_DEFAULT_FILTERS.status],
	};
}

function parseTimekeeperIds(value: string | null): string[] | undefined {
	if (value === null) return undefined;
	if (value === "") return [];
	return Array.from(
		new Set(
			value
				.split(",")
				.map((id) => id.trim())
				.filter(Boolean),
		),
	);
}

function parseQuery(searchParams: URLSearchParams): AdminEntryQuery {
	const today = new Date();
	const from = parseLocalDate(searchParams.get("from"), today);
	const to = parseLocalDate(searchParams.get("to"), from);
	const filters = normalizeFilters(
		filterStateParser.parse(searchParams.get("filter") ?? ""),
	);
	const legacyTimekeeperId = filters.timekeeper;
	delete filters.timekeeper;
	return {
		filters,
		timekeeperIds:
			parseTimekeeperIds(searchParams.get("timekeepers")) ??
			(legacyTimekeeperId ? [legacyTimekeeperId] : undefined),
		from,
		to,
		search: searchParams.get("q") ?? "",
		sorting:
			sortingParser.parse(searchParams.get("sort") ?? "") ?? DEFAULT_SORTING,
	};
}

function appliedSearch(searchParams: URLSearchParams): string {
	const applied = new URLSearchParams();
	for (const key of [
		"run",
		"filter",
		"timekeepers",
		"from",
		"to",
		"q",
		"sort",
	]) {
		const value = searchParams.get(key);
		if (value !== null) applied.set(key, value);
	}
	return applied.toString();
}

function queryKey(query: AdminEntryQuery): {
	where: string;
	sort: string;
} {
	return {
		where: JSON.stringify(buildAdminEntryWhere(query)),
		sort: JSON.stringify(buildAdminEntrySort(query.sorting)),
	};
}

function secondsToHours(seconds: number): number {
	return seconds / 3600;
}

function statsTotals(stats: EntryGroupStats): EntryStatsTotals {
	return {
		tracked: secondsToHours(stats.byStatus.tracked),
		reviewed: secondsToHours(stats.byStatus.reviewed),
		exported: secondsToHours(stats.byStatus.exported),
		billable: secondsToHours(stats.byBillableCategory.billable),
		billableCredit: secondsToHours(stats.byBillableCategory.billableCredit),
		nonBillable: secondsToHours(stats.byBillableCategory.nonBillable),
		unknown: secondsToHours(stats.byBillableCategory.unknown),
		total: secondsToHours(stats.totalSeconds),
	};
}

function optionValues(fieldOptions: FieldOptions, field: EnumFilterField) {
	return normalizeOptions(fieldOptions[field] ?? []).map(
		(option) => option.value,
	);
}

function useFilterMutators(
	draft: AdminEntryQuery,
	setDraft: Dispatch<SetStateAction<AdminEntryQuery>>,
	fieldOptions: FieldOptions,
) {
	const setFilters = useCallback(
		(next: EntryFilterState) =>
			setDraft((current) => ({ ...current, filters: next })),
		[setDraft],
	);
	const setTimekeeperIds = useCallback(
		(timekeeperIds: string[] | undefined) =>
			setDraft((current) => ({ ...current, timekeeperIds })),
		[setDraft],
	);

	const toggle = useCallback(
		(field: EnumFilterField, value: string) => {
			const current = draft.filters;
			if (field === "timekeeper") {
				const selected =
					draft.timekeeperIds ?? optionValues(fieldOptions, field);
				setTimekeeperIds(
					selected.includes(value)
						? selected.filter((item) => item !== value)
						: [...selected, value],
				);
				return;
			}
			const selected = current[field] ?? optionValues(fieldOptions, field);
			const next = selected.includes(value)
				? selected.filter((item) => item !== value)
				: [...selected, value];
			setFilters({ ...current, [field]: next });
		},
		[
			draft.filters,
			draft.timekeeperIds,
			fieldOptions,
			setFilters,
			setTimekeeperIds,
		],
	);

	const selectAll = useCallback(
		(field: EnumFilterField) => {
			if (field === "timekeeper") {
				setTimekeeperIds(undefined);
				return;
			}
			if (field === "status") {
				setFilters({
					...draft.filters,
					status: optionValues(fieldOptions, field),
				});
				return;
			}
			const next = { ...draft.filters };
			delete next[field];
			setFilters(next);
		},
		[draft.filters, fieldOptions, setFilters, setTimekeeperIds],
	);

	const selectNone = useCallback(
		(field: EnumFilterField) => {
			if (field === "timekeeper") {
				setTimekeeperIds([]);
				return;
			}
			setFilters({ ...draft.filters, [field]: [] });
		},
		[draft.filters, setFilters, setTimekeeperIds],
	);

	const reset = useCallback(
		() =>
			setDraft((current) => ({
				...current,
				filters: { status: [...ADMIN_DEFAULT_FILTERS.status] },
				timekeeperIds: undefined,
			})),
		[setDraft],
	);

	return { toggle, selectAll, selectNone, reset };
}

export default function EntriesView() {
	const { makeApiRequest } = useApi();
	const { matters = [], isLoading: mattersLoading } = useMattersCache();
	const [searchParams, setSearchParams] = useSearchParams();
	const appliedSearchParams = appliedSearch(searchParams);
	const applied =
		searchParams.get("run") === "1" ? parseQuery(searchParams) : null;
	const [draft, setDraft] = useState<AdminEntryQuery>(() =>
		parseQuery(searchParams),
	);

	useEffect(() => {
		const next = new URLSearchParams(appliedSearchParams);
		if (next.get("run") === "1") {
			setDraft(parseQuery(next));
		}
	}, [appliedSearchParams]);

	const filterQuery = useQuery({
		queryKey: ["admin_entry_group_filters"],
		queryFn: async () => {
			const response = await makeApiRequest({
				endpoint: "/v2/entry-groups/filters",
				method: "GET",
				queryParams: { scope: "firm" },
			});
			if (response.status === "ERROR") {
				throw new Error(response.error || "Failed to load entry filters");
			}
			return mapAdminEntryFilters(response.data);
		},
		staleTime: 5 * 60 * 1000,
	});
	const fieldOptions = filterQuery.data?.fieldOptions ?? {};
	const filterMutators = useFilterMutators(draft, setDraft, fieldOptions);
	const appliedKey = applied ? queryKey(applied) : null;

	const groupsQuery = useInfiniteQuery({
		queryKey: ["admin_entry_groups", appliedKey?.where, appliedKey?.sort],
		enabled: Boolean(appliedKey),
		initialPageParam: null as string | null,
		queryFn: async ({ pageParam }) => {
			if (!appliedKey) throw new Error("No applied entry query");
			const response = await makeApiRequest({
				endpoint: "/v2/entry-groups",
				method: "GET",
				queryParams: {
					scope: "firm",
					limit: PAGE_LIMIT,
					where: appliedKey.where,
					sort: appliedKey.sort,
					...(pageParam ? { startingAfter: pageParam } : {}),
				},
			});
			if (response.status === "ERROR") {
				throw new Error(response.error || "Failed to load entry groups");
			}
			return response.data as EntryGroupListWire;
		},
		getNextPageParam: (lastPage) =>
			lastPage.hasMore ? (lastPage.data.at(-1)?.id ?? undefined) : undefined,
	});

	const statsQuery = useQuery({
		queryKey: ["admin_entry_group_stats", appliedKey?.where],
		enabled: Boolean(appliedKey),
		queryFn: async () => {
			if (!appliedKey) throw new Error("No applied entry query");
			const response = await makeApiRequest({
				endpoint: "/v2/entry-groups/stats",
				method: "GET",
				queryParams: {
					scope: "firm",
					where: appliedKey.where,
				},
			});
			if (response.status === "ERROR") {
				throw new Error(response.error || "Failed to load entry statistics");
			}
			return mapAdminEntryStats(response.data);
		},
	});

	const entries = useMemo(() => {
		const groups = groupsQuery.data?.pages.flatMap((page) => page.data) ?? [];
		const applications =
			filterQuery.data?.applications ?? new Map<string, string>();
		const mappedMatters = matterMap([
			...(filterQuery.data?.matters ?? []),
			...matters,
		]);
		return groups.map((group: EntryGroupWire) =>
			entryGroupToEntryGroupEntry(group, {
				matters: mappedMatters,
				applications,
			}),
		);
	}, [
		filterQuery.data?.applications,
		filterQuery.data?.matters,
		groupsQuery.data?.pages,
		matters,
	]);

	const timekeepers = filterQuery.data?.timekeepers ?? EMPTY_TIMEKEEPERS;
	const columns = useMemo(
		() =>
			getAutoColumns(timekeepers, { smartTimer: false }).map((column) => ({
				...column,
				enableSorting: ["date", "status", "units", "timekeeper"].includes(
					column.id ?? "",
				),
			})),
		[timekeepers],
	);

	const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
		() => ({
			...(autoVisibilityParser.parse(searchParams.get("cols") ?? "") ??
				AUTO_DEFAULT_COLUMN_VISIBILITY),
			_select: false,
		}),
	);
	const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(
		() =>
			autoColumnOrderParser.parse(searchParams.get("order") ?? "") ??
			AUTO_DEFAULT_COLUMN_ORDER,
	);
	const tableColumnOrder = useMemo(
		() => [
			"_select",
			...columnOrder,
			"client",
			"task",
			"activity",
			"matterName",
			"billable",
			"unitsSeconds",
		],
		[columnOrder],
	);

	const handleSortingChange = useCallback(
		(updater: SortingState | ((current: SortingState) => SortingState)) => {
			setDraft((current) => ({
				...current,
				sorting:
					typeof updater === "function" ? updater(current.sorting) : updater,
			}));
		},
		[],
	);

	const table = useReactTable({
		data: entries,
		columns,
		state: {
			sorting: draft.sorting,
			columnVisibility,
			columnOrder: tableColumnOrder,
			rowSelection: EMPTY_SELECTION,
		},
		onSortingChange: handleSortingChange,
		enableRowSelection: false,
		enableColumnResizing: true,
		columnResizeMode: "onEnd",
		manualSorting: true,
		defaultColumn: { minSize: 40, maxSize: 800 },
		getRowId: (row) => row.id,
		getCoreRowModel: getCoreRowModel(),
	});

	const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
	const toggleExpand = useCallback((id: string) => {
		setExpandedIds((current) => {
			const next = new Set(current);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}, []);

	const runQuery = useCallback(() => {
		const next = new URLSearchParams(searchParams);
		next.set("run", "1");
		next.set("filter", serializeEntryFilters(draft.filters));
		if (draft.timekeeperIds === undefined) next.delete("timekeepers");
		else next.set("timekeepers", draft.timekeeperIds.join(","));
		next.set("from", formatLocalDate(draft.from));
		next.set("to", formatLocalDate(draft.to));
		if (draft.search.trim()) next.set("q", draft.search.trim());
		else next.delete("q");
		next.set("sort", sortingParser.serialize(draft.sorting));
		setSearchParams(next);
	}, [draft, searchParams, setSearchParams]);

	const updateColumns = useCallback(
		(next: VisibilityState) => {
			const withReadOnly = {
				...next,
				_select: false,
			};
			setColumnVisibility(withReadOnly);
			const params = new URLSearchParams(searchParams);
			const encoded = autoVisibilityParser.serialize(withReadOnly);
			if (encoded) params.set("cols", encoded);
			else params.delete("cols");
			setSearchParams(params, { replace: true });
		},
		[searchParams, setSearchParams],
	);

	const updateColumnOrder = useCallback(
		(next: ColumnOrderState) => {
			setColumnOrder(next);
			const params = new URLSearchParams(searchParams);
			const encoded = autoColumnOrderParser.serialize(next);
			if (encoded) params.set("order", encoded);
			else params.delete("order");
			setSearchParams(params, { replace: true });
		},
		[searchParams, setSearchParams],
	);

	const draftKey = JSON.stringify(queryKey(draft));
	const appliedQueryKey = appliedKey ? JSON.stringify(appliedKey) : null;
	const isDirty = draftKey !== appliedQueryKey;
	const error =
		filterQuery.error ?? groupsQuery.error ?? statsQuery.error ?? null;
	const stats = statsQuery.data;

	return (
		<TooltipProvider>
			<div className="flex h-[calc(100vh-1rem)] min-h-0 flex-col bg-background">
				<div className="flex shrink-0 items-center gap-2 border-b border-border px-4 py-3">
					<FilterMenu
						trigger={
							<Button variant="outline" className="gap-2">
								<Filter className="size-4" />
								Filters
							</Button>
						}
						fieldOptions={fieldOptions}
						filters={draft.filters}
						defaultFilters={ADMIN_DEFAULT_FILTERS}
						selectionOverrides={{
							timekeeper: draft.timekeeperIds ?? null,
						}}
						multiSelectFields={["timekeeper"]}
						explicitBulkActionFields={["timekeeper"]}
						onToggleFieldValue={filterMutators.toggle}
						onSelectAll={filterMutators.selectAll}
						onSelectNone={filterMutators.selectNone}
						onReset={filterMutators.reset}
					/>
					<DatePicker
						selectionMode="range"
						dateRange={{ from: draft.from, to: draft.to }}
						onDateChange={(date) =>
							setDraft((current) => ({
								...current,
								from: date,
								to: date,
							}))
						}
						onDateRangeChange={({ from, to }) =>
							setDraft((current) => ({ ...current, from, to }))
						}
						className="w-auto"
					/>
					<SearchBar
						value={draft.search}
						onChange={(search) =>
							setDraft((current) => ({ ...current, search }))
						}
						onClear={() => setDraft((current) => ({ ...current, search: "" }))}
					/>
					<Button
						onClick={runQuery}
						disabled={filterQuery.isLoading}
						className="gap-2"
					>
						<Play className="size-4" />
						Query
						{isDirty ? (
							<span className="size-1.5 rounded-full bg-primary-foreground" />
						) : null}
					</Button>
					<div className="ml-auto flex items-center gap-3">
						{stats ? (
							<span className="text-xs tabular-nums text-muted-foreground">
								{stats.groupCount.toLocaleString()} groups ·{" "}
								{stats.entryCount.toLocaleString()} entries
							</span>
						) : null}
						<ColumnsMenu
							columnVisibility={columnVisibility}
							onColumnVisibilityChange={updateColumns}
							columnOrder={columnOrder}
							onColumnOrderChange={updateColumnOrder}
							labels={AUTO_COLUMN_LABELS}
							defaultOrder={AUTO_DEFAULT_COLUMN_ORDER}
							defaultVisibility={{
								...AUTO_DEFAULT_COLUMN_VISIBILITY,
								_select: false,
							}}
						/>
					</div>
				</div>

				{error ? (
					<div className="border-b border-destructive/30 bg-destructive/10 px-4 py-2 text-sm text-destructive">
						{error instanceof Error ? error.message : "Unable to load entries"}
					</div>
				) : null}

				<div className="min-h-0 flex-1">
					{applied ? (
						<AutoCombineTable
							table={table}
							rowSelection={EMPTY_SELECTION}
							isLoading={
								groupsQuery.isLoading || filterQuery.isLoading || mattersLoading
							}
							expandedIds={expandedIds}
							onToggleExpand={toggleExpand}
							onExpandAll={() =>
								setExpandedIds(new Set(entries.map((entry) => entry.id)))
							}
							onCollapseAll={() => setExpandedIds(new Set())}
							selectedMiniIds={EMPTY_IDS}
							selectedGroupIds={EMPTY_IDS}
							onToggleMini={NOOP}
							onEditMini={NOOP}
							onRowClick={NOOP}
							moveMode={READ_ONLY_MOVE_MODE}
							readOnly
							hasMore={groupsQuery.hasNextPage}
							isLoadingMore={groupsQuery.isFetchingNextPage}
							onLoadMore={() => {
								void groupsQuery.fetchNextPage();
							}}
							emptyMessage="No entry groups match this query."
						/>
					) : (
						<div className="flex h-full items-center justify-center px-6 text-center text-sm text-muted-foreground">
							Choose filters and a date range, then press Query.
						</div>
					)}
				</div>

				{stats ? <StatsBar totals={statsTotals(stats)} /> : null}
			</div>
		</TooltipProvider>
	);
}
